import { AndroidSettings, IOSSettings, NativeSettings } from "capacitor-native-settings";
import {
  AlertTriangle,
  Bell,
  CheckCircle,
  Settings as SettingsIcon,
  Smartphone,
} from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { usePushNotifications } from "@/domains/notifications";
import { isAndroid, isIOS, isNative } from "@/lib/platform";
import { useNotificationPermissionStore } from "@/lib/push-notifications/store/notification-permission-store";

interface NativePushNotificationSettingsProps {
  className?: string;
}

export function NativePushNotificationSettings({ className }: NativePushNotificationSettingsProps) {
  const { t } = useTranslation("settings");
  const [isRequesting, setIsRequesting] = useState(false);
  const [lastError, setLastError] = useState<string | null>(null);

  const { permission, canRequest, requestPermission } = useNotificationPermissionStore();

  const { removeAllNotifications } = usePushNotifications();

  const clearError = () => setLastError(null);

  const handleOpenAppSettings = async () => {
    try {
      await NativeSettings.open({
        optionAndroid: AndroidSettings.ApplicationDetails,
        optionIOS: IOSSettings.App,
      });
    } catch (error) {
      console.error("Failed to open app settings:", error);
      setLastError(
        t(
          "profile.appSettingsError",
          "Unable to open app settings. Please go to your device settings manually."
        )
      );
    }
  };

  const handleRequestPermission = async () => {
    setIsRequesting(true);
    clearError();

    try {
      const success = await requestPermission();
      if (success) {
      } else {
        setLastError(
          t(
            "profile.permissionRequestFailed",
            "Failed to enable push notifications. Please try again."
          )
        );
      }
    } catch (error) {
      setLastError(
        t("profile.permissionRequestError", "An error occurred while requesting permission.")
      );
      console.error("Permission request failed:", error);
    } finally {
      setIsRequesting(false);
    }
  };

  const _handleTestNotification = async () => {
    // For native apps, we can clear delivered notifications as a test
    try {
      await removeAllNotifications();
    } catch (error) {
      console.error("Failed to test notifications:", error);
    }
  };

  const getPlatformInfo = () => {
    if (isIOS()) return { name: "iOS" };
    if (isAndroid()) return { name: "Android" };
    return { name: "Native" };
  };

  const getPermissionStatus = () => {
    // Only show badge for positive states, errors are handled by alerts below
    switch (permission) {
      case "granted":
        return {
          icon: <CheckCircle className="h-3 w-3" />,
          label: t("profile.browserNotificationsDescription.enabled"),
          badgeClassName:
            "bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200 border-green-300 dark:border-green-700",
        };
      case "prompt":
        return {
          icon: <Bell className="h-3 w-3" />,
          label: t("profile.browserNotificationsDescription.notConfigured"),
          badgeClassName:
            "bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-200 border-orange-300 dark:border-orange-700",
        };
      default:
        // For denied/unsupported, don't show badge - use alerts instead
        return null;
    }
  };

  const permissionStatus = getPermissionStatus();
  const platformInfo = getPlatformInfo();

  return (
    <div className={className}>
      {/* Native Push Notifications */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <div className="flex items-center gap-2">
              <Smartphone className="h-4 w-4 text-muted-foreground" />
              <span className="font-medium">{t("profile.browserNotifications")}</span>
            </div>

            <div className="space-y-1">
              {/* Only show badge for positive states */}
              {permissionStatus && (
                <div className="flex items-center gap-2">
                  <Badge className={permissionStatus.badgeClassName}>
                    {permissionStatus.icon}
                    <span className="ml-1">{permissionStatus.label}</span>
                  </Badge>
                </div>
              )}

              {/* Show platform info only */}
              {isNative() && (
                <div className="text-xs text-muted-foreground">Platform: {platformInfo.name}</div>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            {canRequest && (
              <Button size="sm" onClick={handleRequestPermission} disabled={isRequesting}>
                {isRequesting
                  ? t("profile.browserNotificationsActions.requesting")
                  : t("profile.browserNotificationsActions.enable")}
              </Button>
            )}

            {/* Always show settings button for native apps */}
            {isNative() && (
              <Button size="sm" variant="outline" onClick={handleOpenAppSettings} className="gap-1">
                <SettingsIcon className="h-3 w-3" />
                {t("profile.appSettings", "App Settings")}
              </Button>
            )}
          </div>
        </div>

        {/* Error Alert */}
        {lastError && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>{lastError}</AlertDescription>
          </Alert>
        )}

        {/* Unsupported Alert */}
        {permission === "unsupported" && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              {t(
                "profile.notificationsUnsupportedMessage",
                "Push notifications are not supported on this device."
              )}
            </AlertDescription>
          </Alert>
        )}

        {/* Denied Permission Alert */}
        {permission === "denied" && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              {t(
                "profile.notificationsDeniedMessage",
                'Push notifications are currently disabled. Use the "App Settings" button above to enable them in your device settings.'
              )}
            </AlertDescription>
          </Alert>
        )}
      </div>
    </div>
  );
}
